import { Camera, CheckCircle2, Info, Upload, User, X } from "lucide-react";
import { useRef, useState } from "react";
import Button from "@/components/ui/Button";
import { FieldWrapper } from "@/components/ui/FieldWrapper";
import type { MandateForm, SetField } from "../types";
import SignaturePad from "./SignaturePad";
import UploadBlock from "./UploadBlock";

interface Props {
  form: MandateForm;
  setField: SetField;
  errors: Record<string, string | undefined>;
}

const ID_TYPE_OPTIONS = [
  "Aadhaar card",
  "PAN card",
  "Voter ID",
  "Driving Licence",
  "Passport",
].map((v) => ({ value: v, label: v }));

const ADDRESS_TYPE_OPTIONS = ["Aadhaar card", "Electricity bill", "Rent agreement", "Passport"].map((v) => ({
  value: v,
  label: v,
}));

export default function VerificationStep({ form, setField, errors }: Props) {
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [capture, setCapture] = useState(false);
  const [photoName, setPhotoName] = useState("");
  const photoError = errors?.customerPhoto;
  const sigError = errors?.signature;

  function handlePhoto(file?: File | null) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setField("customerPhoto", String(reader.result));
      setPhotoName(file.name);
    };
    reader.readAsDataURL(file);
  }

  return (
    <div>
      <h3 className="text-lg font-semibold text-ink">Customer Verification</h3>
      <p className="text-[13px] text-ink-secondary">
        Capture the customer's photograph, documents and signature
      </p>

      <div className="mt-5 flex items-start gap-3 rounded-lg border border-info/30 bg-info-bg p-3.5">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-info">
          <Info className="h-5 w-5" />
        </span>
        <p className="text-[13px] text-ink">
          All documents must be clear and readable. Blurred or incomplete uploads may cause the
          mandate to be rejected by the bank.
        </p>
      </div>

      {/* Customer Photograph */}
      <div id="customerPhoto" className="mt-5 rounded-[10px] border border-line bg-white p-5">
        <div className="mb-1 flex items-center gap-1">
          <span className="text-sm font-medium text-ink">Customer Photograph</span>
          <span className="text-danger">*</span>
        </div>
        <p className="text-xs text-ink-secondary">
          A clear photograph of the customer's face taken at your counter
        </p>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="relative h-[140px] w-[140px] shrink-0 overflow-hidden rounded-[10px] border border-dashed border-slate-300 bg-page">
            {form.customerPhoto ? (
              <>
                <img src={form.customerPhoto} alt="Customer photograph" className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => {
                    setField("customerPhoto", "");
                    setPhotoName("");
                  }}
                  className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-md bg-white/90 text-ink-muted hover:text-danger cursor-pointer"
                  aria-label="Remove photograph"
                >
                  <X className="h-4 w-4" />
                </button>
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <User className="h-9 w-9 text-slate-400" />
              </div>
            )}
          </div>

          <div className="flex flex-1 flex-col items-start gap-2.5">
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture={capture ? "user" : undefined}
              className="hidden"
              onChange={(e) => {
                handlePhoto(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button
              variant="secondary"
              fullWidth
              leftIcon={<Camera className="h-4 w-4" />}
              onClick={() => {
                setCapture(true);
                photoInputRef.current?.click();
              }}
            >
              Take Photo
            </Button>
            <Button
              variant="secondary"
              fullWidth
              leftIcon={<Upload className="h-4 w-4" />}
              onClick={() => {
                setCapture(false);
                photoInputRef.current?.click();
              }}
            >
              Upload Photo
            </Button>
            <p className="text-xs text-ink-secondary">JPG or PNG, maximum 2 MB</p>
            {form.customerPhoto && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
                <CheckCircle2 className="h-4 w-4" />
                Photo captured
              </span>
            )}
          </div>
        </div>

        {photoError && <p className="mt-2 text-xs text-danger">{photoError}</p>}
      </div>

      {/* Document uploads */}
      <div className="mt-5 flex flex-col gap-4">
        <UploadBlock
          id="idProofType"
          label="ID Proof"
          helper="Aadhaar card, PAN card, voter ID, driving licence or passport"
          docTypeOptions={ID_TYPE_OPTIONS}
          docTypeValue={form.idProofType}
          onDocTypeChange={(v) => setField("idProofType", v)}
          fileValue={form.idProofFileName}
          onFileChange={(f) => setField("idProofFileName", f)}
          fileUrlValue={form.idProofUrl}
          onFileUrlChange={(url) => setField("idProofUrl", url)}
          error={errors?.idProofType || errors?.idProofFileName}
        />
        <UploadBlock
          id="addressProofType"
          label="Address Proof"
          helper="Aadhaar card, electricity bill, rent agreement or passport"
          docTypeOptions={ADDRESS_TYPE_OPTIONS}
          docTypeValue={form.addressProofType}
          onDocTypeChange={(v) => setField("addressProofType", v)}
          fileValue={form.addressProofFileName}
          onFileChange={(f) => setField("addressProofFileName", f)}
          fileUrlValue={form.addressProofUrl}
          onFileUrlChange={(url) => setField("addressProofUrl", url)}
          error={errors?.addressProofType || errors?.addressProofFileName}
        />
      </div>

      {/* Customer Signature */}
      <div id="signature" className="mt-5 rounded-[10px] border border-line bg-white p-5">
        <div className="mb-1 flex items-center gap-1">
          <span className="text-sm font-medium text-ink">Customer Signature</span>
          <span className="text-danger">*</span>
        </div>
        <p className="text-xs text-ink-secondary">
          Ask the customer to sign inside the box using a finger or stylus
        </p>
        <div className="mt-4">
          <SignaturePad value={form.signature} onChange={(dataUrl) => setField("signature", dataUrl)} />
        </div>
        {sigError && <p className="mt-2 text-xs text-danger">{sigError}</p>}
      </div>
    </div>
  );
}